import request from './request';
import { PaginatedResponse } from './admin';

export interface FoodCategory {
  id: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
  isEnabled: boolean;
  children?: FoodCategory[];
}

export interface FoodItem {
  id: string;
  categoryId: string;
  name: string;
  aliases: string[];
  caloriesPer100g: number;
  proteinPer100g: number;
  fatPer100g: number;
  carbsPer100g: number;
  source: string;
  isEnabled: boolean;
  createdAt: string;
}

export interface FoodUnit {
  id: string;
  foodItemId: string;
  unitName: string;
  grams: number;
  description: string;
  isDefault: boolean;
}

export function getCategoryTree(): Promise<FoodCategory[]> {
  return request.get('/api/admin/v1/foods/categories');
}

export function createCategory(data: {
  name: string;
  parentId?: string | null;
  sortOrder?: number;
}): Promise<FoodCategory> {
  return request.post('/api/admin/v1/foods/categories', data);
}

export function updateCategory(
  id: string,
  data: { name?: string; parentId?: string | null; sortOrder?: number; isEnabled?: boolean }
): Promise<void> {
  return request.put(`/api/admin/v1/foods/categories/${id}`, data);
}

export function getFoodItems(params: {
  categoryIds?: string;
  keyword?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<FoodItem>> {
  return request.get('/api/admin/v1/foods/items', { params });
}

export function getFoodItem(id: string): Promise<FoodItem> {
  return request.get(`/api/admin/v1/foods/items/${id}`);
}

export function createFoodItem(
  data: Omit<FoodItem, 'id' | 'createdAt' | 'isEnabled'>
): Promise<FoodItem> {
  return request.post('/api/admin/v1/foods/items', data);
}

export function updateFoodItem(
  id: string,
  data: Partial<FoodItem>
): Promise<void> {
  return request.put(`/api/admin/v1/foods/items/${id}`, data);
}

export function deleteFoodItem(id: string): Promise<void> {
  return request.delete(`/api/admin/v1/foods/items/${id}`);
}

export function batchUpdateFoodItemStatus(ids: string[], isEnabled: boolean): Promise<void> {
  return request.post('/api/admin/v1/foods/items/batch-status', { ids, isEnabled });
}

export function getFoodUnits(foodItemId: string): Promise<FoodUnit[]> {
  return request.get(`/api/admin/v1/foods/items/${foodItemId}/units`);
}

export function createFoodUnit(
  foodItemId: string,
  data: Omit<FoodUnit, 'id' | 'foodItemId'>
): Promise<FoodUnit> {
  return request.post(`/api/admin/v1/foods/items/${foodItemId}/units`, data);
}
